import React, { useState } from "react";
import { Tag, Dropdown, InputNumber, Modal, message } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { MenuProps } from "antd";

interface PaymentStatusBadgeProps {
  status: "paid" | "unpaid" | "partial";
  amount: number;
  currency: "RSD" | "EUR";
  paidAmount?: number;
  remainingAmount?: number;
  onStatusChange: (
    status: "paid" | "unpaid" | "partial",
    paidAmount?: number,
  ) => Promise<void>;
  disabled?: boolean;
}

export const PaymentStatusBadge: React.FC<PaymentStatusBadgeProps> = ({
  status,
  amount,
  currency,
  paidAmount = 0,
  remainingAmount = 0,
  onStatusChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [partialPaidAmount, setPartialPaidAmount] = useState<number>(
    paidAmount || 0,
  );
  const [loading, setLoading] = useState(false);

  const handleStatusClick = async (
    newStatus: "paid" | "unpaid" | "partial",
  ) => {
    if (newStatus === "partial") {
      setIsModalVisible(true);
      return;
    }

    setLoading(true);
    try {
      await onStatusChange(newStatus);
      message.success(t("Payment status updated successfully"));
    } catch (error) {
      console.error("Error updating payment status:", error);
      message.error(t("Failed to update payment status"));
    } finally {
      setLoading(false);
    }
  };

  const handlePartialSubmit = async () => {
    if (
      !partialPaidAmount ||
      partialPaidAmount <= 0 ||
      partialPaidAmount >= amount
    ) {
      message.warning(
        t("Paid amount must be greater than 0 and less than total"),
      );
      return;
    }

    setLoading(true);
    try {
      await onStatusChange("partial", partialPaidAmount);
      message.success(t("Payment status updated successfully"));
      setIsModalVisible(false);
    } catch (error) {
      console.error("Error updating payment status:", error);
      message.error(t("Failed to update payment status"));
    } finally {
      setLoading(false);
    }
  };

  const menuItems: MenuProps["items"] = [
    {
      key: "paid",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CheckCircleOutlined style={{ color: "#52c41a" }} />
          <span>{t("Paid")}</span>
        </div>
      ),
      onClick: () => handleStatusClick("paid"),
    },
    {
      key: "unpaid",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
          <span>{t("Unpaid")}</span>
        </div>
      ),
      onClick: () => handleStatusClick("unpaid"),
    },
    {
      key: "partial",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ClockCircleOutlined style={{ color: "#faad14" }} />
          <span>{t("Partially paid")}</span>
        </div>
      ),
      onClick: () => handleStatusClick("partial"),
    },
  ];

  const getStatusConfig = () => {
    switch (status) {
      case "paid":
        return {
          color: "success",
          icon: <CheckCircleOutlined />,
          text: t("Paid"),
        };
      case "unpaid":
        return {
          color: "error",
          icon: <CloseCircleOutlined />,
          text: t("Unpaid"),
        };
      case "partial":
        return {
          color: "warning",
          icon: <ClockCircleOutlined />,
          text: t("Partially paid"),
        };
    }
  };

  const config = getStatusConfig();

  return (
    <>
      <Dropdown
        menu={{ items: menuItems }}
        disabled={disabled || loading}
        trigger={["click"]}
      >
        <Tag
          icon={config.icon}
          color={config.color}
          style={{
            cursor: disabled ? "default" : "pointer",
            fontSize: 13,
            padding: "4px 10px",
            borderRadius: 6,
          }}
        >
          {config.text}
          {status === "partial" && (
            <div style={{ fontSize: 11, marginTop: 2 }}>
              {paidAmount.toLocaleString()} / {amount.toLocaleString()}{" "}
              {currency}
            </div>
          )}
        </Tag>
      </Dropdown>

      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <DollarOutlined />
            {t("Enter paid amount")}
          </div>
        }
        open={isModalVisible}
        onOk={handlePartialSubmit}
        onCancel={() => setIsModalVisible(false)}
        okText={t("Confirm")}
        cancelText={t("Cancel")}
        className="standardHeader standardFooter"
        confirmLoading={loading}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 8 }}>
            {t("Total amount")}:{" "}
            <strong>
              {amount.toLocaleString()} {currency}
            </strong>
          </div>
          <InputNumber
            style={{ width: "100%" }}
            placeholder={t("Enter paid amount")}
            value={partialPaidAmount}
            onChange={(value) => setPartialPaidAmount(value || 0)}
            min={0.01}
            max={amount - 0.01}
            precision={2}
            addonAfter={currency}
          />
          <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 8 }}>
            {t("Remaining")}:{" "}
            <strong>
              {(amount - (partialPaidAmount || 0)).toLocaleString()} {currency}
            </strong>
          </div>
        </div>
      </Modal>
    </>
  );
};
